export enum ConfigDisplayTab {
  COMPANY = "companies",
  INSTRUMENT = "instruments",
  DATASET = "datasets",
  API_KEYS = "api-keys",
}

export enum SectorLevel {
  L1 = 1,
  L2 = 2,
  L3 = 3,
}

export type Exchange = {
  id: number
  name: string
  mic: string
}

export type Currency = {
  id: number
  name: string
  code: string
}

export type SubRegion = {
  id: number
  name: string
  m49_code: string
}

export type Region = {
  id: number
  name: string
  m49_code: string
  sub_regions: SubRegion[]
}

export enum Cyclicality {
  CYCLICAL = "cyclical",
  NEUTRAL = "neutral",
  DEFENSIVE = "defensive",
  UNKNOWN = "unknown",
}

export type Country = {
  id: number
  name: string
  code: string
}

export type Instrument = {
  id: number
  instrument_content_type: number
  instrument_id: number
  exchange: Exchange
  bloomberg_code: string
  currency: Currency
  sedol: string
  bb_unique: string
  in_user_universe: boolean
}

export enum InstrumentType {
  STOCK = "stock",
  ETF = "etf",
  INDEX = "index",
}

export const INSTRUMENT_ASSET_TYPES = [
  { value: "equity", label: "Equity" },
  { value: "bond", label: "Bond" },
  { value: "monetary", label: "Monetary" },
  { value: "alternative", label: "Alternative" },
  { value: "unknown", label: "Unknown" },
] as const

export type InstrumentAssetType = (typeof INSTRUMENT_ASSET_TYPES)[number]["value"]

export type InstrumentLight = {
  id: number
  bloomberg_code: string
  exchange_country_code: string
  in_user_universe: boolean
  latest_price_usd: number
  latest_price: number
  latest_price_latency: string
  type: InstrumentType
}

/**
 * Light response for company list endpoint (GET /companies/)
 */
export type TradingCompany = {
  id: number
  name: string
  bb_company: string | null
  cyclicality: Cyclicality
  main_instrument_ticker: string | null
  bbg_ticker: string | null // Alias for main_instrument_ticker from backend
}

/**
 * Full response for company detail endpoint (GET /companies/<id>/)
 * Includes all related instruments (direct + derivatives)
 */
export type TradingCompanyDetail = TradingCompany & {
  headquarter_country: Country | null
  reporting_currency: Currency | null
  sectors: Sector[]
  instruments: Instrument[]
  all_related_instruments: Instrument[]
  created_at: string
  updated_at: string
}

export type Sector = {
  id: number
  name: string
  level: SectorLevel
}

/**
 * Full response for instrument detail endpoint (GET /instruments/<id>/)
 */
export type InstrumentDetail = {
  id: number
  bloomberg_code: string
  bb_unique: string | null
  sedol: string | null
  exchange: Exchange | null
  currency: Currency
  company: TradingCompany | null
  underlying: InstrumentLight | null
  country: Country | null
  country_issue: Country | null
  security_name: string | null
  security_type: string | null
  security_type2: string | null
  asset_type: InstrumentAssetType
  exchange_market_status: string | null
  active: boolean
  core: boolean
  trading_day_end_time: string | null
  issuer: string | null
  rtg_sp: string | null
  rtg_moody: string | null
  rtg_fitch: string | null
  fund_euro_direct_ucits: boolean | null
  fund_total_assets: number | null
  fund_leverage: boolean | null
  is_main_company_listing: boolean
  history_requested: boolean
  // Derivatives data
  maturity: string | null
  // Price data (set by PriceService)
  eod_price: number | null
  eod_price_usd: number | null
  latest_price: number | null
  latest_price_usd: number | null
  latest_price_latency: "live" | "delayed" | "eod" | null
  multiplication_factor: number | null
  // Editable
  sectors: Sector[]
  can_edit_sectors: boolean
  created_at: string
  updated_at: string
}

export type InstrumentUpdatePayload = {
  sectors: { id: number; level: number }[]
  asset_type?: InstrumentAssetType
}
